/**
 * Handle messages from outside the editor (i.e the viewer) and internal error messages.
 *
 * To display an error message in the UI set the StackFileName Atom to
 * a utility stackFileName and update the ReplaceCode atom with a different value from it's current value.
 * The CodeAtom will take are of returning the message as rYaml and the ReplaceCode atom will force CodeMirror to
 * re-render. Code Mirror is uncontrolled so it's necessary to bend the rules of React a little. But that's what
 * rules are for.
 * @category Functional Components
 * @module Messages
 */

import { AiChatLevelAtom, InlineEditTargetAtom } from "../state/aiChatAtoms";
import {
	CurrentFilterColorsAtom,
	HostId,
	ParsedStackAtom,
	ViewerPageIndex,
} from "../state/atoms";
import {
	FilterNamesSelector,
	FiltersAreLoaded,
	PreviewerFontCSS,
} from "../state/filters";
import {
	NavigationTypeAtom,
	ShowStacksDuringDemoMode,
	StackCfKeyAtom,
	StoryNamesAtom,
} from "../state/stackAtoms";
import { useCallback, useEffect } from "react";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";

import { AllFiles } from "../state/files";
import { ComposedStack } from "../state/stackSelectors";
import { type Message, demoImages } from "@quickpoint.me/shared";
import { TogglePanelFor } from "../state/selectors";
import { DemoMode } from "../state/uiAtoms";
import { authorReceiveBC } from "../lib/messageBus";
import { handlePostMessage } from "../lib/messageHandler";
import { useFileApi } from "../hooks/useFileApi";
import { useStackApi } from "../hooks/useStackApi";

/**
 * @export Messages
 * @returns  A React Fragment
 */
function Messages() {
	const hostId = useRecoilValue(HostId);
	const rawFiles = useRecoilValue(AllFiles);
	const demoMode = useRecoilValue(DemoMode);
	const allFiles = demoMode ? demoImages : rawFiles;
	const filterNames = useRecoilValue(FilterNamesSelector);
	const setStackCfKey = useSetRecoilState(StackCfKeyAtom);
	const setStoryNames = useSetRecoilState(StoryNamesAtom);
	const setNavigationType = useSetRecoilState(NavigationTypeAtom);
	const setParsedStack = useSetRecoilState(ParsedStackAtom);
	const setFiltersAreLoaded = useSetRecoilState(FiltersAreLoaded);
	const setPreviewerFontCSS = useSetRecoilState(PreviewerFontCSS);
	const stack = useRecoilValue(ComposedStack);
	const togglePanelFor = useSetRecoilState(TogglePanelFor);
	const toggleShowStacksDuringDemoMode = useRecoilCallback(({ set }) => () => {
		set(ShowStacksDuringDemoMode, (prev) => !prev);
	});

	const setCurrentFilterColors = useSetRecoilState(CurrentFilterColorsAtom);
	const currentFilterColors = useRecoilValue(CurrentFilterColorsAtom);
	const viewerPageIndex = useRecoilValue(ViewerPageIndex);
	const setViewerPageIndex = useSetRecoilState(ViewerPageIndex);

	const setInlineEditTarget = useSetRecoilState(InlineEditTargetAtom);
	const setChatLevel = useSetRecoilState(AiChatLevelAtom);

	const { fetchAllFiles, deleteImageFromQP } = useFileApi();
	const { saveStackToDb, deleteStackFromDb } = useStackApi();

	const setError = useCallback(
		(error: string) => {
			console.error("Messages.tsx error", error);
			setStackCfKey(error);
		},
		[setStackCfKey],
	);

	useEffect(() => {
		if (!authorReceiveBC) return;

		const deps = {
			allFiles,
			currentFilterColors,
			deleteImageFromQP,
			deleteStackFromDb,
			fetchAllFiles,
			filterNames,
			hostId,
			saveStackToDb,
			setChatLevel,
			setCurrentFilterColors,
			setError,
			setFiltersAreLoaded,
			setInlineEditTarget,
			setNavigationTypeAtom: setNavigationType,
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			setParsedStack: setParsedStack as any,
			setPreviewerFontCSS,
			setStackCfKey,
			setStoryNamesAtom: setStoryNames,
			stack,
			togglePanelFor,
			toggleShowStacksDuringDemoMode,
			viewerPageIndex,
			setViewerPageIndex,
		};

		const handleMessage = (e: MessageEvent<Message>) =>
			handlePostMessage(e, deps);

		authorReceiveBC.onmessage = handleMessage;

		return () => {
			if (authorReceiveBC) {
				authorReceiveBC.onmessage = null;
			}
		};
	}, [
		fetchAllFiles,
		deleteStackFromDb,
		deleteImageFromQP,
		saveStackToDb,
		setParsedStack,
		hostId,
		toggleShowStacksDuringDemoMode,
		allFiles,
		filterNames,
		stack,
		setFiltersAreLoaded,
		setPreviewerFontCSS,
		setStoryNames,
		setNavigationType,
		togglePanelFor,
		setError,
		setStackCfKey,
		setCurrentFilterColors,
		currentFilterColors,
		viewerPageIndex,
		setViewerPageIndex,
		setInlineEditTarget,
		setChatLevel,
	]);

	return <></>;
}

export default Messages;
